import React, { useState, useEffect } from 'react'
import { useSelector } from 'react-redux'
import get from 'lodash/get'
import keyBy from 'lodash/keyBy'

import { Loading } from '@fielded/shared-ui'

import { selectMasterData } from '../../../../root/reducers/shipments/master-data'
import { isReturnShipment } from '../../../../common/utils/shipment'
import { hasFeature } from '../../../../van-shared/utils/features'
import { getPlannedQuantities, makeDeliveryItems, makeDeliveryItemsV2 } from '../../common/makeDeliveryItems'
import { batchIdToProductId } from '../../common/utils'
import PSMDeliveryConfirmation from './PSMDeliveryConfirmation'

const PSMDeliveryConfirmationContainer = ({
  api,
  user,
  config,
  shipment,
  formRef,
  comment,
  isOnline,
  onCommentChange,
  onProceedClick
}) => {
  const masterData = useSelector(selectMasterData)

  const [location, setLocation] = useState()
  const [items, setItems] = useState([])
  const [name, setName] = useState('')
  const [signature, setSignature] = useState('')
  const [loading, setLoading] = useState(true)

  const productsById = get(masterData, 'products.byId', {})
  const useShipmentPrices = hasFeature(config, 'features.useShipmentPrices')

  let locationId = get(shipment, 'destination.id')
  if (isReturnShipment(null, shipment)) {
    locationId = get(shipment, 'origin.id')
  }

  const fetchData = async () => {
    try {
      let location = await api.location.get(locationId)
      if (!location && isOnline) {
        // fetch from agave — race against a timeout so a hanging request on a
        // degraded connection doesn't leave the driver stuck on the loading screen
        const agaveFetch = api.location.getLocationsViaIds([locationId])
        let timeoutId
        const agaveTimeout = new Promise((resolve, reject) => {
          timeoutId = setTimeout(() => reject(new Error('location fetch timed out')), 8000)
        })
        try {
          const locations = await Promise.race([agaveFetch, agaveTimeout])
          location = locations[0]
        } catch (agaveError) {
          console.warn('Could not fetch location from Agave, proceeding without it:', agaveError)
        } finally {
          clearTimeout(timeoutId)
        }
      }
      const productIds = [...new Set(Object.keys(get(shipment, 'counts', {})).map(batchIdToProductId))]
      const priceReferenceDate = get(shipment, 'orderCreatedDate') || get(shipment, 'snapshotDates.new') || get(shipment, 'snapshotDates.packed') || get(shipment, 'snapshotDates.sent') || get(shipment, 'snapshotDates.arrived') || get(shipment, 'snapshotDates.received')
      const { prices = [] } = productIds.length > 0
        ? await api.price.list({ skus: productIds, referenceDate: priceReferenceDate })
        : { prices: [] }
      const productPricesById = keyBy(prices, 'sku')

      const plannedQuantities = getPlannedQuantities(shipment)
      const makeItems = useShipmentPrices ? makeDeliveryItemsV2 : makeDeliveryItems
      const items = makeItems({ shipment, productsById, productPricesById, plannedQuantities, config }) || []
      setLocation(location)
      setItems(items)
    } catch (error) {
      console.warn(error)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchData()
  }, [])

  if (loading) {
    return <Loading loadingText='Loading delivery note …' />
  }

  return (
    <PSMDeliveryConfirmation
      config={config}
      formRef={formRef}
      shipment={shipment}
      location={location}
      items={items}
      name={name}
      signature={signature}
      comment={comment}
      onCommentChange={onCommentChange}
      onProceed={onProceedClick}
      setName={setName}
      setSignature={setSignature}
    />
  )
}

export default PSMDeliveryConfirmationContainer
